import { createSelector } from 'reselect';

import * as types from '../actions/actionTypes';
import { getUserSelectedFashionSize } from './userdata';

/**
 * Reducer to keep the currently selected sewing pattern in state.
 *
 * @param state
 * @param action
 * @returns {*}
 */
export default (state, action) => {
    const defaultOptions = new Set();
    defaultOptions.add('default');
    switch (action.type) {
        case types.SEWPAT_SELECTED:
            return Object.assign({}, state, {
                sewpatId: action.sewpatId,
                selectedByUser: true,
                options: defaultOptions,
            });

        case types.SEWPAT_SETTED:
            // In case of a user selected value,
            // we don't set the sewpat by business logic.
            if (state.selectedByUser === true) {
                return state;
            }
            return Object.assign(
                {},
                state,
                {
                    sewpatId: action.sewpatId,
                    options: defaultOptions,
                },
            );

        case types.NAVIGATED_TO_SEWPAT_VIEW:
            if (state.selectedByUser === true) {
                return state;
            }
            return Object.assign({}, state, {
                sewpatId: (
                    action.payload || {}
                ).sewpatId,
                options: defaultOptions,
            });

        case types.FASHION_SIZE_SELECTED:
            return Object.assign({}, state, {
                size: action.size,
            });

        case types.SEWPAT_OPTIONS_SELECTED:
            return Object.assign({}, state, {
                options: action.options,
            });

        default:
            return state;
    }
};

/**
 * Reducer for changing the language specific sewpat data.
 *
 * @param state
 * @param action
 * @param languages
 * @returns {*}
 */
export const sewpats = (state, action, languages) => {
    switch (action.type) {
        case types.LOCALE_CHANGED:
            return Object.assign({}, languages[action.currentLanguage].sewpats);

        case types.LOCALE_SETTED_FROM_URI:
            return Object.assign({}, languages[action.language].sewpats);

        default:
            return state;
    }
};

export const getSewpats = state => state.stolo.i18n.sewpats;

export const getActiveSewpatId = state => {
    if (state.stolo.sewpat.selectedByUser) {
        return state.stolo.userdata.selectedSewpatId;
    }
    if ((
        state.location.payload || {}
    ).sewpatId) {
        return state.location.payload.sewpatId;
    }
    return state.stolo.userdata.selectedSewpatId || state.stolo.sewpat.sewpatId;
};

export const getSewpatIdByURI = state => (
    state.location.payload || {}
).sewpatId || null;

/**
 * Returns the sewing patterns recommended fabric id.
 */
export const getRecommendedFabricId = createSelector(
    [getActiveSewpatId, getSewpats],
    (sewpatId, sewpats) => sewpats[sewpatId].recommendedFabricId,
);

/**
 * Returns the sewing patterns recommended fabric id.
 */
export const getSewpatFigurineDimension = createSelector(
    [getActiveSewpatId, getSewpats], (sewpatId, sewpats) => (
        {
            width: sewpats[sewpatId].figuri.width,
            height: sewpats[sewpatId].figuri.height,
            width2: sewpats[sewpatId].figuri.width2,
            height2: sewpats[sewpatId].figuri.height2,
        }
    ));

/**
 * Returns the active sewpats figurine overlay.
 */
export const getSewpatFigurineOverlay = createSelector(
    [getActiveSewpatId, getSewpats], (sewpatId, sewpats) => (
        sewpats[sewpatId].figurineOverlay
    ));

/**
 * Returns the sewing patterns recommended fabric id.
 */
export const getSewpatFigurineOverlayDimension = createSelector(
    [getActiveSewpatId, getSewpats], (sewpatId, sewpats) => (
        {
            width: sewpats[sewpatId].galleryOverlay.width,
            height: sewpats[sewpatId].figurineOverlay.height,
            width2: sewpats[sewpatId].figurineOverlay.width2,
            height2: sewpats[sewpatId].figurineOverlay.height2,
        }
    ));

/**
 * Returns the active sewpats gallery overlay.
 */
export const getSewpatGalleryOverlay = createSelector(
    [getActiveSewpatId, getSewpats], (sewpatId, sewpats) => (
        sewpats[sewpatId].galleryOverlay
    ));

/**
 * Returns the sewing patterns recommended fabric id.
 */
export const getSewpatGalleryOverlayDimension = createSelector(
    [getActiveSewpatId, getSewpats], (sewpatId, sewpats) => (
        {
            width: sewpats[sewpatId].galleryOverlay.width,
            height: sewpats[sewpatId].galleryOverlay.height,
            width2: sewpats[sewpatId].galleryOverlay.width2,
            height2: sewpats[sewpatId].galleryOverlay.height2,
        }
    ));

/**
 * Returnst the active sewing pattern object.
 */
export const getActiveSewpat = createSelector(
    [getActiveSewpatId, getSewpats],
    (sewpatId, sewpats) => sewpats[sewpatId],
);

/**
 * Returnst the active sewing pattern object.
 */
export const getNavigationSewpat = createSelector(
    [getSewpatIdByURI, getActiveSewpatId, getSewpats],
    (sewpatURI, sewpatId, sewpats) => sewpatURI
        ? sewpats[sewpatURI]
        : sewpats[sewpatId],
);

/**
 * Returnst the active sewing pattern object.
 */
export const getNavigationSewpatTitle = createSelector(
    [getSewpatIdByURI, getActiveSewpatId, getSewpats],
    (sewpatURI, sewpatId, sewpats) => sewpatURI
        ? sewpats[sewpatURI].title
        : sewpats[sewpatId].title,
);

/**
 * Returns the active sewing pattern title
 */
export const getActiveSewpatTitle = createSelector([
    getActiveSewpatId, getSewpats], (sewpatId, sewpats) => {
    const sewpat = sewpats[sewpatId];
    return sewpat.title;
});

/**
 * Returns the active sewing pattern selected values data.
 */
export const getActiveSewpatSelectedValues = createSelector([
    getActiveSewpatId, getSewpats], (sewpatId, sewpats) => {
    const sewpat = sewpats[sewpatId];
    return sewpat.selectedValues;
});

export const getSelectedSewpatOptions = state => new Set(state.stolo.sewpat.options);
export const sewpatHasMultipleOptions = sewpat => Object.keys(sewpat.options).length > 1;

export const getFabricLengthBySize = (sewpat, size, options) => {
    if (!sewpat || !options) {
        return null;
    }
    const sewpatOptions = sewpat.options;
    let length = 0;
    for (const optionId of options) {
        length += sewpatOptions[optionId]['fabric-length'][size];
    }
    return length;
};

export const getFabricDisplayLengthBySize = (sewpat, size, options) => {
    if (!sewpat || !options) {
        return null;
    }
    const sewpatOptions = sewpat.options;
    let length = 0;
    for (const optionId of options) {
        length += sewpatOptions[optionId]['fabric-display-length'][size];
    }
    return length;
};

const getSelectedSize = state => state.stolo.sewpat.size;

export const getFashionSize = createSelector(
    [getUserSelectedFashionSize, getSelectedSize],
    (userSize, selectedSize) => selectedSize || userSize || 42,
);

export const getSewpatSizes = createSelector(
    [getActiveSewpat],
    sewpat => sewpat.sizes,
);

/**
 * Returns the active sewing pattern options.
 */
export const getActiveSewpatOptions = createSelector([
    getActiveSewpatId, getSewpats], (sewpatId, sewpats) => {
    const sewpat = sewpats[sewpatId];
    return sewpat.options;
});



// WEBPACK FOOTER //
// ./src/reducers/sewpats.js